// Veroilmoitus 9A: rows and summaries from the server's profit_report_summary (#1571).
//
// The server (#1570, transactions/tax_9a.py) computes the 9A rule: per sell row `tax_9a`
// {profit, assumption_costs} and the totals in `tax_9a_summary` split by the security's
// tax treatment. This module only arranges those values for the table; it does no
// arithmetic on money, so the page shows exactly what the Excel export contains.
import { roundScaleDecimals } from "services/helpers"

export const TAX_TREATMENT_INVESTMENT = "investment"
export const TAX_TREATMENT_PERSONAL_USE = "personal_use"
export const TAX_TREATMENT_HOUSEHOLD_GOODS = "household_goods"
export const TAX_TREATMENTS = [
  TAX_TREATMENT_INVESTMENT,
  TAX_TREATMENT_PERSONAL_USE,
  TAX_TREATMENT_HOUSEHOLD_GOODS,
]

const identity = (key, text) => text

export const taxTreatmentOf = (row) => {
  const value = row?.related_model?.[0]?.tax_treatment
  return TAX_TREATMENTS.includes(value) ? value : TAX_TREATMENT_INVESTMENT
}

const has = (obj, key) => obj && obj[key] !== undefined && obj[key] !== null

// Display values of one sell row. Profit and the purchase cost assumption come from the
// server; the price/cost columns are the row's own unless the assumption replaced them.
export const sellRowDisplay = (row) => {
  const tax9a = row?.tax_9a || {}
  const assumptionCosts = has(tax9a, "assumption_costs") ? Number(tax9a.assumption_costs) : 0
  const useAssumption = assumptionCosts > 0
  const pick = (serverKey, rowKey) => {
    if (has(tax9a, serverKey)) return tax9a[serverKey]
    return useAssumption ? 0 : row?.[rowKey]
  }
  const profit = has(tax9a, "profit") ? tax9a.profit : null
  return {
    ...row,
    profit: profit,
    profit_use: profit,
    assumption_costs: assumptionCosts,
    purchase_cost_use: pick("purchase_cost", "purchase_cost"),
    commission_total_buy_use: pick("commission_buy", "commission_total_buy"),
    commission_total_sell_use: pick("commission_sell", "commission_total_sell"),
  }
}

export const splitSellRows = (rows) => {
  const result = {
    [TAX_TREATMENT_INVESTMENT]: [],
    [TAX_TREATMENT_PERSONAL_USE]: [],
    [TAX_TREATMENT_HOUSEHOLD_GOODS]: [],
  }
  ;(rows || []).forEach((row) => {
    result[taxTreatmentOf(row)].push(sellRowDisplay(row))
  })
  return result
}

const money = (value) => roundScaleDecimals(value, 2)

const summaryRow = (label, values = {}) => ({
  stock__name: label,
  ...values,
  type: "summary",
  className: "bg-light",
})

// Voitot yhteensä / Tappiot yhteensä / Tulos of the investment sales, as before #1571.
export const investmentSummaryRows = (investment, t = identity) => {
  if (!investment) return []
  const side = (s) => ({
    purchase_cost_use: money(investment[`purchase_cost_${s}`]),
    profit_use: money(investment[`profit_${s}`]),
    price_total: money(investment[`price_total_${s}`]),
    commission_total_sell_use: money(investment[`commission_sell_${s}`]),
    commission_total_buy_use: money(investment[`commission_buy_${s}`]),
    assumption_costs: money(investment[`assumption_costs_${s}`]),
  })
  return [
    summaryRow(t("tax_table_progit_total", "Voitot yhteensä"), side("positive")),
    summaryRow(t("tax_table_losses_total", "Tappiot yhteensä"), side("negative")),
    summaryRow(t("tax_table_total", "Tulos"), { profit_use: money(investment.result) }),
  ]
}

// Personal-use and household-goods sections ({title, rows}) and the taxable total.
// A section is shown only when it has sales; without special sales this returns [].
export const specialSections = (rowsByTreatment, summary, t = identity) => {
  if (!summary) return []
  const sections = []
  const personal = rowsByTreatment?.[TAX_TREATMENT_PERSONAL_USE] || []
  const household = rowsByTreatment?.[TAX_TREATMENT_HOUSEHOLD_GOODS] || []
  const lossesLabel = t("tax_table_special_losses", "Tappiot yhteensä (ei vähennyskelpoinen)")

  if (personal.length > 0) {
    const pu = summary.personal_use || {}
    sections.push({
      title: t("tax_table_personal_use_title", "Henkilökohtaiseen käyttöön tarkoitettu omaisuus (TVL 50 § 2 mom.)"),
      rows: [
        ...personal,
        summaryRow(t("tax_table_personal_use_gains", "Voitot yhteensä (veronalainen)"), { profit_use: money(pu.gains) }),
        summaryRow(lossesLabel, { profit_use: money(pu.losses) }),
      ],
    })
  }
  if (household.length > 0) {
    const hg = summary.household_goods || {}
    sections.push({
      title: t("tax_table_household_title", "Tavanomainen koti-irtaimisto (TVL 48 § 1 mom.)"),
      rows: [
        ...household,
        summaryRow(t("tax_table_household_gains", "Voitot yhteensä"), { profit_use: money(hg.gains) }),
        summaryRow(t("tax_table_household_exempt", "Verovapaa osuus (enintään 5 000 € / vuosi)"), { profit_use: money(hg.exempt) }),
        summaryRow(t("tax_table_household_taxable", "Veronalainen osuus"), { profit_use: money(hg.taxable) }),
        summaryRow(lossesLabel, { profit_use: money(hg.losses) }),
      ],
    })
  }
  if (sections.length > 0) {
    sections.push({
      title: null,
      rows: [
        summaryRow(t("tax_table_taxable_total", "Veronalaiset luovutusvoitot yhteensä"), { profit_use: money(summary.taxable_total) }),
      ],
    })
  }
  return sections
}

// Everything the page needs from one profit_report_summary response.
export const buildTax9aView = (profitSummary, t = identity) => {
  const empty = { sellRows: [], summaryRows: [], sections: [] }
  if (!profitSummary) return empty
  const byTreatment = splitSellRows(profitSummary.sell)
  const summary = profitSummary.tax_9a_summary
  if (!summary) {
    // eslint-disable-next-line no-console
    console.warn("profit_report_summary without tax_9a_summary; 9A totals are not shown")
    return { ...empty, sellRows: byTreatment[TAX_TREATMENT_INVESTMENT] }
  }
  return {
    sellRows: byTreatment[TAX_TREATMENT_INVESTMENT],
    summaryRows: investmentSummaryRows(summary.investment, t),
    sections: specialSections(byTreatment, summary, t),
  }
}

// Wraps an async fetcher so that only the latest call's result is delivered: a slow
// earlier response must not overwrite a newer one (assumption settings refetch, #1571).
export const latestOnly = (fetcher) => {
  let latest = 0
  return (...args) => {
    const call = ++latest
    return Promise.resolve(fetcher(...args)).then((result) => ({
      stale: call !== latest,
      result,
    }))
  }
}
